import { formatCurrency } from '@eriko/shared-types';
import { Package } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { formatPeriodLabel } from './utils';

interface Transaction {
  date: string | null;
  docNumber: string | null;
  amount: number;
  memo: string | null;
}

interface Vendor {
  vendorId: string;
  name: string;
  total: number;
  transactionCount: number;
  transactions: Transaction[];
}

interface Data {
  type: string;
  mode?: 'qbo' | 'xero' | 'local';
  period: string;
  currency?: string;
  vendors: Vendor[];
  grandTotal: number;
}

const FX_MEMO_RE =
  /Original amount:\s*[€$£¥]?\s*([\d.,]+)\s*([A-Z]{3})?[\s,]+Converted amount:\s*[€$£¥]?\s*([\d.,]+)\s*([A-Z]{3})/i;
const ORIG_ONLY_RE = /^Original amount:\s*([\d.,]+)\s*([A-Z]{3})?\s*$/i;

function formatTxnDate(date: string | null): string {
  if (!date) return '—';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
}

function compactMemo(memo: string | null): { compact: string; kind: 'fx' | 'orig' } | null {
  if (!memo) return null;
  const fx = memo.match(FX_MEMO_RE);
  if (fx) {
    const [, origAmt, origCur, , dstCur] = fx;
    const from = origCur ? `${origCur} ${origAmt}` : origAmt;
    return { compact: `${from} → ${dstCur}`, kind: 'fx' };
  }
  const orig = memo.match(ORIG_ONLY_RE);
  if (orig) {
    const [, origAmt, origCur] = orig;
    return { compact: `${origCur ? `${origCur} ${origAmt}` : origAmt}`, kind: 'orig' };
  }
  return null;
}

export function ExpensesByVendorReport({ data }: { data: Record<string, unknown> }) {
  const d = data as unknown as Data;
  const periodLabel = formatPeriodLabel(d.period);
  const isEmpty = d.vendors.length === 0;
  const grandTotal = Number(d.grandTotal ?? 0);
  const currency = d.currency ?? 'USD'; // home currency for correct symbol (CUR-1)
  const vendors = [...(d.vendors ?? [])].sort((a, b) => b.total - a.total);
  const totalTxns = vendors.reduce((s, v) => s + v.transactionCount, 0);

  return (
    <div className="overflow-hidden rounded-[10px] border border-surface-200 bg-surface-0 transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b border-neg/15 bg-gradient-to-r from-neg-surface to-transparent px-4.5 py-3.5">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-neg-surface ring-1 ring-neg-border">
            <Package className="h-3.5 w-3.5 text-neg" strokeWidth={2} />
          </span>
          <span className="text-sm font-semibold text-surface-900">
            {d.mode === 'local' ? 'Expenses by Merchant' : 'Expenses by Vendor'}
          </span>
          {!isEmpty && (
            <span className="text-[12px] text-surface-500 tabular-nums">
              · {vendors.length} {vendors.length === 1 ? 'vendor' : 'vendors'} · {totalTxns} txns
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {d.mode === 'local' && (
            <span className="rounded-full bg-info-surface px-2.5 py-0.75 text-xs font-medium text-info ring-1 ring-info-border">
              Local ledger
            </span>
          )}
          {periodLabel && (
            <span className="rounded-full bg-surface-0 px-2.5 py-0.75 text-xs font-medium text-surface-600 ring-1 ring-surface-200">
              {periodLabel}
            </span>
          )}
        </div>
      </div>

      {isEmpty ? (
        <div className="px-4.5 py-10 text-center text-[13px] text-surface-500">
          No expenses recorded for this period.
        </div>
      ) : (
        <Accordion type="multiple">
          {vendors.map((v) => {
            const pct = grandTotal > 0 ? (v.total / grandTotal) * 100 : 0;
            const barWidth = Math.max(Math.min(pct, 100), pct > 0 ? 1.5 : 0);
            const tier: 'lead' | 'major' | 'minor' =
              pct >= 10 ? 'lead' : pct >= 5 ? 'major' : 'minor';
            const barColor =
              tier === 'lead'
                ? 'var(--color-neg)'
                : tier === 'major'
                  ? 'color-mix(in oklch, var(--color-neg) 65%, transparent)'
                  : 'color-mix(in oklch, var(--color-neg) 35%, transparent)';
            const pctTextClass =
              tier === 'lead'
                ? 'text-neg font-semibold'
                : tier === 'major'
                  ? 'text-neg/80'
                  : 'text-neg/55';
            const pctLabel =
              pct >= 10 ? `${pct.toFixed(0)}%` : pct >= 1 ? `${pct.toFixed(1)}%` : '<1%';

            return (
              <AccordionItem
                key={v.vendorId}
                value={v.vendorId}
                className="border-b border-surface-100 last:border-b-0 data-[state=open]:bg-neg-surface/60"
              >
                <AccordionTrigger className="flex w-full items-center gap-3 px-4.5 py-2.5 text-left select-none transition-colors hover:bg-neg-surface/40 hover:no-underline data-[state=open]:bg-transparent">
                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <div className="flex items-baseline gap-2">
                      <span className="truncate text-[13px] font-semibold text-surface-900">
                        {v.name}
                      </span>
                      <span className="shrink-0 rounded-full bg-surface-100 px-1.5 py-0.25 text-[10px] font-medium text-surface-600 tabular-nums">
                        {v.transactionCount} {v.transactionCount === 1 ? 'txn' : 'txns'}
                      </span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-neg-surface ring-1 ring-inset ring-neg/10">
                      <div
                        className="h-full rounded-full transition-[width] duration-500 ease-out"
                        style={{ width: `${barWidth}%`, backgroundColor: barColor }}
                      />
                    </div>
                  </div>
                  <div className="flex shrink-0 items-baseline gap-2.5">
                    <span className={`w-12 text-right font-mono text-[12px] tabular-nums ${pctTextClass}`}>
                      {pctLabel}
                    </span>
                    <span className="font-mono text-[13px] font-semibold tabular-nums text-neg">
                      {formatCurrency(v.total, 2, currency)}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-1 pt-0">
                  <div className="ml-4.5 mr-2 mb-1 overflow-hidden rounded-md bg-surface-0 ring-1 ring-surface-100">
                    <ul className="divide-y divide-surface-100">
                      {v.transactions.map((t, i) => {
                        const compact = compactMemo(t.memo);
                        return (
                          <li
                            key={i}
                            className="flex items-center gap-2.5 px-3 py-1.75 transition-colors hover:bg-neg-surface/40"
                          >
                            <span className="w-12 shrink-0 font-mono text-[11px] tabular-nums text-surface-500">
                              {formatTxnDate(t.date)}
                            </span>
                            {t.docNumber && (
                              <span className="shrink-0 rounded bg-warn-surface px-1.5 py-0.25 font-mono text-[10px] tabular-nums text-warn ring-1 ring-warn-border">
                                #{t.docNumber}
                              </span>
                            )}
                            {compact ? (
                              <span
                                className="flex shrink-0 items-center gap-1 rounded bg-info-surface px-1.5 py-0.25 font-mono text-[10px] text-info ring-1 ring-info-border"
                                title={t.memo ?? undefined}
                              >
                                <span className="text-[9px] font-bold uppercase tracking-wider opacity-70">
                                  {compact.kind}
                                </span>
                                <span>{compact.compact}</span>
                              </span>
                            ) : t.memo ? (
                              <span
                                className="flex-1 truncate text-[12px] text-surface-700"
                                title={t.memo}
                              >
                                {t.memo}
                              </span>
                            ) : (
                              <span className="flex-1 text-[12px] text-surface-300">—</span>
                            )}
                            {compact && <span className="flex-1" />}
                            <span className="shrink-0 font-mono text-[12px] font-semibold tabular-nums text-surface-900">
                              {formatCurrency(t.amount, 2, currency)}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      {/* Grand Total footer */}
      <div className="flex items-center border-t border-neg/20 bg-gradient-to-r from-neg-surface to-transparent px-4.5 py-3 text-sm font-semibold">
        <span className="flex-1 text-surface-900">Total Expenses</span>
        <span className="font-mono text-[15px] font-bold tabular-nums text-neg">
          {formatCurrency(grandTotal, 2, currency)}
        </span>
      </div>
    </div>
  );
}
